/**
 * Puts a Playwright run into the position its route describes. Seeding lives
 * here rather than in the app shell so it can be exercised without a browser,
 * and so the shell keeps one statement about e2e instead of the whole recipe.
 */

import { useConfigurationStore } from '../stores/useConfigurationStore';
import { usePcfValidationStore } from '../stores/usePcfValidationStore';
import { usePluginUpdateStore } from '../stores/usePluginUpdateStore';
import { useUIStore } from '../stores/useUIStore';
import {
  buildE2eNavigationHistory,
  buildE2ePluginUpdateSnapshot,
  type E2eBootstrapConfig,
} from './e2eBootstrap';
import { applyE2eSetupRevisionFixture } from './e2eSetupRevisionFixture';

/**
 * Pre-answers the standard-parameters landing prompt. Left unanswered it opens
 * a modal on every mapping-workspace landing and aria-hides the workspace from
 * role-based queries. "Include" matches the legacy default; an answer the route
 * already carries is never overwritten.
 */
function answerStandardParametersPrompt(): void {
  const configuration = useConfigurationStore.getState();
  if (
    configuration.processPropertiesConfiguration.includeDefaultPipeexParameters === null
  ) {
    configuration.setIncludeDefaultPipeexParameters(true);
  }
}

/**
 * Seeds the plugin-update prompt. The embedded gRPC server is absent in e2e, so
 * a real check would only ever resolve to CHECK_FAILED. Seeding synchronously
 * marks the store initialized, so the prompt's mount-time check is a no-op.
 */
function seedPluginUpdate(config: E2eBootstrapConfig): void {
  if (!config.pluginUpdate) {
    return;
  }

  usePluginUpdateStore.getState().applySnapshot(buildE2ePluginUpdateSnapshot(config.pluginUpdate));
}

function seedWorkflowPosition(config: E2eBootstrapConfig): void {
  const { setSelectedOption, jumpTo } = useUIStore.getState();

  if (config.workflowOption) {
    setSelectedOption(config.workflowOption);
  }

  if (config.workspaceTab) {
    usePcfValidationStore.getState().requestWorkspaceTab(config.workspaceTab);
  }

  if (config.screen) {
    jumpTo(config.screen, buildE2eNavigationHistory(config.screen, config.workflowOption));
  }
}

export function applyE2eBootstrap(config: E2eBootstrapConfig): void {
  seedWorkflowPosition(config);
  seedPluginUpdate(config);
  answerStandardParametersPrompt();

  // Landing on a screen is not an edit. Seeding through the ordinary setters
  // marks the configuration unsaved, which would make every bootstrapped screen
  // open behind the unsaved-changes gate and hide the state under test.
  useConfigurationStore.setState({ isDirty: false });

  // Runs last: reopening setup reads the history seeded above to decide which
  // step the revision falls back to.
  applyE2eSetupRevisionFixture(config);
}
